import React from 'react'

import { func, array, bool, number } from 'prop-types'

import CollapsibleSection from '../CollapsibleSection'
import AddEmbedModal from '../Modals/AddEmbedModal'
import AddMarmosetEmbedModal from '../Modals/AddMarmosetEmbedModal'
import GenerateViewerModal from '../Modals/GenerateViewerModal'
import EmbedsList from './Components/EmbedsList'

const POLLING_INTERVAL = 5000

class EmbedsArea extends React.Component {
  constructor() {
    super()

    this.state = {
      modalIsOpen: false,
      marmosetModalIsOpen: false,
      generateViewerModalIsOpen: false,
      activeEmbed: null,
      viewerGenerationStatus: 'idle',
    }

    this.pollingInterval = null
    this.isInitialCheck = false
    this.abortController = null

    this.openModal = this.openModal.bind(this)
    this.closeModal = this.closeModal.bind(this)
    this.handleEmbedUrlAdd = this.handleEmbedUrlAdd.bind(this)
    this.handleEmbedFilesAdd = this.handleEmbedFilesAdd.bind(this)
    this.handleStartGeneration = this.handleStartGeneration.bind(this)
    this.pollViewerGenerationStatus = this.pollViewerGenerationStatus.bind(this)
  }

  componentDidMount() {
    const { itemId } = this.props

    if (itemId) {
      this.checkInitialGenerationStatus()
    }
  }

  componentWillUnmount() {
    this.stopPolling()
    if (this.abortController) {
      this.abortController.abort()
    }
  }

  checkInitialGenerationStatus() {
    this.isInitialCheck = true
    this.fetchViewerGenerationStatus()
  }

  fetchViewerGenerationStatus() {
    const { itemId, onViewerGenerated } = this.props

    this.abortController = new AbortController()

    fetch(`/api/internal/items/${itemId}/viewer-generation-status`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
      },
      credentials: 'same-origin',
      signal: this.abortController.signal,
    })
      .then(response => response.json())
      .then(data => {
        this.setState({ viewerGenerationStatus: data.status })

        if (data.status === 'pending') {
          this.startPolling()
        } else if (data.status === 'completed') {
          this.stopPolling()
          // Only notify when generation completes during session, not on page reload
          if (!this.isInitialCheck && onViewerGenerated) {
            onViewerGenerated(data.embed)
          }
        } else if (data.status === 'idle') {
          // Generation failed and BE reset status - stop polling
          this.stopPolling()
        }

        this.isInitialCheck = false
      })
      .catch(error => {
        if (error.name === 'AbortError') return

        this.setState({ viewerGenerationStatus: 'idle' })
      })
  }

  startPolling() {
    if (this.pollingInterval) return

    this.pollingInterval = setInterval(this.pollViewerGenerationStatus, POLLING_INTERVAL)
  }

  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval)
      this.pollingInterval = null
    }
  }

  pollViewerGenerationStatus() {
    this.fetchViewerGenerationStatus()
  }

  openModal(embed) {
    const { disableMainDropZone } = this.props

    if (embed.slug === 'marmoset') {
      this.setState({ marmosetModalIsOpen: true, activeEmbed: embed })
    } else if (embed.slug === 'generate-viewer') {
      this.setState({ generateViewerModalIsOpen: true, activeEmbed: embed })
    } else {
      this.setState({ modalIsOpen: true, activeEmbed: embed })
    }

    disableMainDropZone(true)
  }

  closeModal() {
    const { disableMainDropZone } = this.props

    this.setState({
      modalIsOpen: false,
      marmosetModalIsOpen: false,
      generateViewerModalIsOpen: false,
      activeEmbed: null,
    })

    disableMainDropZone(false)
  }

  handleEmbedUrlAdd(url) {
    const { addEmbed } = this.props

    addEmbed(url)
    this.closeModal()
  }

  handleEmbedFilesAdd(files) {
    const { onDrop } = this.props

    onDrop(files)
    this.closeModal()
  }

  handleStartGeneration() {
    const { itemId } = this.props

    this.closeModal()
    this.setState({ viewerGenerationStatus: 'pending' })

    this.abortController = new AbortController()

    fetch(`/api/internal/items/${itemId}/generate-viewer`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
      },
      credentials: 'same-origin',
      signal: this.abortController.signal,
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          this.startPolling()
        } else {
          this.setState({ viewerGenerationStatus: 'idle' })
          App.events.publish('app:notification:flash:error', data.error)
          if (window.Sentry) {
            window.Sentry.captureException(new Error(data.error))
          }
        }
      })
      .catch(error => {
        if (error.name === 'AbortError') return

        this.setState({ viewerGenerationStatus: 'idle' })
        App.events.publish('app:notification:flash:error', 'Failed to start viewer generation')
        if (window.Sentry) {
          window.Sentry.captureException(error)
        }
      })
  }

  render() {
    const { deleteEmbed, embeds, hasSupportedFileTypes, onChange, onDragStateChange } = this.props
    const {
      activeEmbed,
      generateViewerModalIsOpen,
      marmosetModalIsOpen,
      modalIsOpen,
      viewerGenerationStatus,
    } = this.state

    const isGenerating = viewerGenerationStatus === 'pending'
    const hasAutoGeneratedViewer = embeds.some(
      embed => embed.externalId?.startsWith('auto_generated_')
    )

    return (
      <div className="publisher-area publisher-area--embeds">
        <div className="card">
          <CollapsibleSection sectionSlug="embeds" trigger="Embeds" hasTooltip>
            <EmbedsList
              key={`${embeds.length}-embeds-list`}
              embeds={embeds}
              openModal={this.openModal}
              deleteEmbed={deleteEmbed}
              onChange={onChange}
              onDragStateChange={onDragStateChange}
              hasSupportedFileTypes={hasSupportedFileTypes}
              isGenerating={isGenerating}
              hasAutoGeneratedViewer={hasAutoGeneratedViewer}
            />

            {activeEmbed && modalIsOpen && (
              <AddEmbedModal
                modalIsOpen={modalIsOpen}
                embedTitle={activeEmbed.title}
                closeModal={this.closeModal}
                addEmbed={this.handleEmbedUrlAdd}
              />
            )}

            {activeEmbed && marmosetModalIsOpen && (
              <AddMarmosetEmbedModal
                modalIsOpen={marmosetModalIsOpen}
                closeModal={this.closeModal}
                addFiles={this.handleEmbedFilesAdd}
              />
            )}

            {activeEmbed && generateViewerModalIsOpen && (
              <GenerateViewerModal
                modalIsOpen={generateViewerModalIsOpen}
                closeModal={this.closeModal}
                handleStartGeneration={this.handleStartGeneration}
              />
            )}
          </CollapsibleSection>
        </div>
      </div>
    )
  }
}

EmbedsArea.propTypes = {
  addEmbed: func.isRequired,
  deleteEmbed: func.isRequired,
  embeds: array.isRequired,
  onDrop: func.isRequired,
  onChange: func.isRequired,
  disableMainDropZone: func.isRequired,
  onDragStateChange: func.isRequired,
  hasSupportedFileTypes: bool,
  itemId: number,
  onViewerGenerated: func,
}

EmbedsArea.defaultProps = {
  hasSupportedFileTypes: false,
  itemId: null,
  onViewerGenerated: () => {},
}

export default EmbedsArea
